import { CAP, STATE, type Sim } from '../../sim/sim';
import { TIPS_KEY } from './settings';
import { advance, guideReady, chooseTip, countTip, countsOnRead, parseTips, startGuide, stepLine, STEPS, tipLine, tipsInput, placeGuide, placeArrow, inView, type Step, type Tip } from './tips';

/** Sheep Tag's nonblocking step and success beat, adapted to this HUD; its arrow only lands on something real (tips.ts placeArrow). */
export class GuideView {
  private saved = (() => { try { return parseTips(localStorage.getItem(TIPS_KEY)); } catch { return parseTips(null); } })();
  private flow = startGuide();
  private match: Sim | null = null;
  private countedMatch = false;
  private bubble = document.createElement('aside');
  private line = document.createElement('span');
  private live = document.createElement('span');
  private close = document.createElement('button');
  private marker = document.createElement('div');
  private pointer?: string;
  private tip: Tip | null = null;
  private until = 0;
  private visible = false;
  private exitAnimation?: Animation;
  private tipRead = 0;
  private tipCounted = false;
  /** Tips read through this match without being counted (the group tip waits for its hold): not again until the next. */
  private rested = new Set<Tip>();
  /** The open group tip was showing its folded first step (Tap Groups) last frame. */
  private tipFolded = false;
  private fightSeen = false;
  private marchRead = 0;
  constructor(root: HTMLElement) {
    this.bubble.className = 'guide-bubble'; this.bubble.hidden = true;
    this.live.className = 'vh'; this.live.setAttribute('aria-live', 'polite'); this.live.setAttribute('aria-atomic', 'true');
    this.close.addEventListener('click', () => {
      if (this.tip) { this.countTip(); this.tip = null; this.until = performance.now() + 4000; }
      else { this.flow = advance(this.flow, 'skip'); this.saved.done = true; this.save(); }
      this.hide();
    });
    this.bubble.append(this.line, this.close);
    this.marker.innerHTML = '<svg viewBox="0 0 32 32" aria-hidden="true"><path d="M3 11H17V4L29 16 17 28V21H3Z" style="fill: var(--gold); stroke: var(--navy)" stroke-width="3" stroke-linejoin="round"/></svg>';
    this.marker.className = 'guide-marker'; this.marker.hidden = true; this.marker.setAttribute('aria-hidden', 'true');
    root.append(this.bubble, this.marker, this.live);
    addEventListener('pointerdown', e => { this.pointer = e.pointerType; }, true);
  }
  private save() { try { localStorage.setItem(TIPS_KEY, JSON.stringify(this.saved)); } catch { /* session state holds */ } }
  private countTip() {
    if (!this.tip || this.tipCounted) return;
    this.saved = countTip(this.saved, this.tip); this.save(); this.tipCounted = true;
  }
  replay(s: Sim) { if (this.match !== s) this.countedMatch = false; this.match = s; this.flow = startGuide(); this.tip = null; this.fightSeen = false; this.marchRead = 0; this.tipRead = 0; this.tipCounted = false; this.rested.clear(); }
  /** The player did what tip t teaches (a hold opened a group's card): it counts as seen, and an open one closes. */
  acted(t: Tip) {
    this.saved = countTip(this.saved, t); this.save();
    if (this.tip === t) { this.tipCounted = true; this.tip = null; this.until = performance.now() + 4000; }
  }
  action(action: Step) { if (!this.flow.ended && !this.tip) this.flow = advance(this.flow, action); }
  get guiding() { return !this.flow.ended; }
  private hide(clearPulse = true) {
    this.visible = false; this.marker.hidden = true; this.bubble.dataset.targeted = 'false';
    if (!this.bubble.hidden && !this.exitAnimation) {
      if (matchMedia('(prefers-reduced-motion: reduce)').matches || !this.bubble.animate) this.bubble.hidden = true;
      else {
        this.bubble.inert = true;
        this.bubble.getAnimations().forEach(animation => animation.cancel());
        const animation = this.bubble.animate([{ opacity: 1 }, { opacity: 0 }], { duration: 120, easing: 'ease-out' });
        this.exitAnimation = animation;
        animation.onfinish = () => { this.bubble.hidden = true; this.exitAnimation = undefined; };
      }
    }
    if (clearPulse) document.querySelectorAll('.guide-pulse').forEach(e => e.classList.remove('guide-pulse'));
  }
  /** at: where a unit is drawn (between sim steps), so an arrow at a soldier lands on his model, not his next step. */
  frame(s: Sim, me: number, enabled: boolean, playing: boolean, dt: number,
    project: (x: number, z: number) => { x: number; y: number } | null, selected: number,
    at: (i: number) => readonly [number, number] = i => [s.x[i], s.y[i]]) {
    this.visible = false; this.marker.hidden = true;
    this.render(s, me, enabled, playing, dt, project, selected, at);
    if (!this.visible) this.hide();
  }
  private render(s: Sim, me: number, enabled: boolean, playing: boolean, dt: number,
    project: (x: number, z: number) => { x: number; y: number } | null, selected: number, at: (i: number) => readonly [number, number]) {
    if (!playing || me < 0 || !s.players[me]?.hero || s.players[me].out) return;
    if (this.match !== s) {
      this.match = s; this.flow = startGuide(); this.flow.ended = this.saved.done;
      this.countedMatch = false; this.tip = null; this.fightSeen = false; this.marchRead = 0; this.tipRead = 0; this.tipCounted = false; this.rested.clear();
    }
    if (!enabled) { this.tip = null; return; }
    if (!this.countedMatch) { this.saved.matches++; this.countedMatch = true; this.save(); }
    let fighting = false, walking = false;
    const soldiers: number[] = []; // the first few, for an arrow at one the player can see
    for (let i = 0; i < CAP; i++) {
      if (s.state[i] === STATE.dead) continue;
      if (s.team[i] === me) {
        if (s.heroOf[i] < 0) { if (soldiers.length < 12) soldiers.push(i); walking ||= s.state[i] === STATE.walk; }
        if (s.target[i] >= 0 || s.baseTarget[i] >= 0 || (s.hp[i] < s.maxHp[i] && s.calm[i] < 3)) fighting = true;
      } else if (s.target[i] >= 0 && s.team[s.target[i]] === me) fighting = true;
    }
    this.fightSeen ||= fighting;
    const busy = !!document.querySelector('.sheet:not([hidden]), .toast.on');
    this.flow = advance(this.flow, null, dt);
    if (this.flow.ended && !this.saved.done) { this.saved.done = true; this.save(); }
    const hero = s.players[me].hero!, hi = hero.i;
    let text = '', target = '', spots: { x: number; y: number }[] = [], folded = false;
    const input = tipsInput(this.pointer, matchMedia('(pointer: coarse)').matches, matchMedia('(any-pointer: fine)').matches);
    if (!this.flow.ended) {
      const step = STEPS[this.flow.index];
      this.bubble.dataset.step = step;
      if (step === 'tier' && s.players[me].tier >= 4) this.action('tier');
      if (!guideReady(step, { enabled, fighting, busy, tier: s.canTier(me), skill: step === 'skill' && s.canCastFirstNow(me), selected: selected > 0, fightSeen: this.fightSeen }, this.flow.beat > 0)) return;
      text = this.flow.beat ? '✓ Done' : stepLine(step, input, innerHeight <= 500);
      const b = s.bases[me];
      if (!this.flow.beat) {
        // World targets: a marching soldier (home while none has spawned), any soldier, home. Send names Center.
        const marching = soldiers.map(i => { const [x, y] = at(i); return { x, y }; });
        spots = step === 'march' ? [...marching, b] : step === 'select' ? marching : step === 'retreat' ? [b] : [];
        target = ({ send: '.goal-btns .goal:nth-child(1)', tier: '.tier', skill: '.skill', retreat: '.goal-btns .goal:nth-child(2)' } as Partial<Record<Step, string>>)[step] ?? '';
      }
      this.close.textContent = 'Skip';
    } else {
      if (fighting || busy) return;
      const ready = { salve: hi >= 0 && s.hp[hi] < s.maxHp[hi] * .4 && s.salveT[hi] <= 0, group: !!document.querySelector('.group[data-empty="false"]') };
      // a tip whose reason has gone (the salve bought, every grouped soldier fallen) goes, neither counted nor rested
      if (this.tip && !ready[this.tip]) this.tip = null;
      if (this.tip && this.tipRead >= 7) { if (!this.tipCounted) this.rested.add(this.tip); this.tip = null; this.until = performance.now() + 4000; }
      if (!this.tip && performance.now() >= this.until) {
        this.tip = chooseTip(this.saved, { enabled, guiding: false, fighting, busy,
          salve: ready.salve && !this.rested.has('salve'), group: ready.group && !this.rested.has('group') });
        if (this.tip) { this.tipRead = 0; this.tipCounted = false; this.tipFolded = false; }
      }
      if (!this.tip) return;
      // a portrait phone folds the groups behind one Groups button (controls.ts fan): name and point at the tap that
      // shows them until they fan out, then at the group to hold
      folded = this.tip === 'group' && !!document.querySelector<HTMLElement>('.hud .gtoggle[aria-expanded="false"]')?.checkVisibility?.();
      // the Groups tap is the folded tip's first step: the hold it then asks for gets its own read time (Flash re-check F1)
      if (this.tipFolded && !folded) this.tipRead = 0;
      this.tipFolded = folded;
      text = tipLine(this.tip, innerHeight <= 500, folded); target = this.tip === 'salve' ? '.shop' : folded ? '.gtoggle' : '.group[data-empty="false"]';
      this.close.textContent = 'Close';
    }
    const entering = this.bubble.hidden || !!this.exitAnimation;
    this.exitAnimation?.cancel(); this.exitAnimation = undefined;
    this.bubble.inert = false;
    this.bubble.hidden = false; this.bubble.style.left = '0px';
    this.bubble.dataset.compact = 'false';
    if (this.line.textContent !== text) this.line.textContent = text;
    this.bubble.hidden = false;
    // Measured placement, like Sheep Tag's marker: never cover the HUD or its open sheets.
    const obstacles = [...document.querySelectorAll<HTMLElement>([
      '.hud .tl-row', '.hud .board', '.hud .minimap', '.hud .hb-info',
      '.hud .goal-now:not([hidden])', '.hud button', '.hud .toast.on', '.sheet:not([hidden])',
    ].join(', '))]
      .filter(e => e.getClientRects().length).map(e => e.getBoundingClientRect());
    const lit = target ? document.querySelector(`.hud ${target}${this.tip === 'salve' ? '' : ':not(:disabled)'}`) : null;
    document.querySelectorAll('.guide-pulse').forEach(e => { if (e !== lit) e.classList.remove('guide-pulse'); });
    lit?.classList.add('guide-pulse');
    const bounds = lit?.getBoundingClientRect();
    const world = bounds ? undefined : spots.find(p => { const q = project(p.x, p.y); return q && inView(innerWidth, innerHeight, q, obstacles); });
    const point = bounds ? { x: bounds.left + bounds.width / 2, y: bounds.top + bounds.height / 2, bounds }
      : world ? project(world.x, world.y) : null;
    const size = innerWidth <= 700 || innerHeight <= 500 ? 28 : 32;
    // Only a target an arrow can reach shapes the card: a crowded control keeps the full copy, not the compact one.
    const reachable = !!point && !!placeArrow(innerWidth, innerHeight, size, { left: -1e4, top: -1e4, width: 0, height: 0 }, point, obstacles);
    const fits = (w: number, h: number) => (x: number, y: number) => !reachable || !!placeArrow(innerWidth, innerHeight, size,
      { left: x, top: y, width: w, height: h }, point!, obstacles);
    const measured = this.bubble.getBoundingClientRect();
    let r = { width: this.bubble.offsetWidth || measured.width, height: this.bubble.offsetHeight || measured.height };
    let place = placeGuide(innerWidth, innerHeight, r.width, r.height, obstacles, fits(r.width, r.height));
    if (!place) {
      this.bubble.dataset.compact = 'true';
      text = this.tip ? tipLine(this.tip, true, folded) : this.flow.beat ? '✓ Done' : stepLine(STEPS[this.flow.index], input, true);
      this.line.textContent = text;
      const measuredSmall = this.bubble.getBoundingClientRect();
      const small = { width: this.bubble.offsetWidth || measuredSmall.width, height: this.bubble.offsetHeight || measuredSmall.height };
      r = small;
      place = placeGuide(innerWidth, innerHeight, r.width, r.height, obstacles, fits(r.width, r.height));
    }
    // A crowded target must not suppress the lesson itself.
    place ??= placeGuide(innerWidth, innerHeight, r.width, r.height, obstacles);
    if (!place) { this.bubble.hidden = true; this.hide(); return; }
    this.bubble.style.left = `${place.x}px`; this.bubble.style.top = `${place.y}px`; this.visible = true;
    if (entering && !matchMedia('(prefers-reduced-motion: reduce)').matches) this.bubble.animate?.([{ opacity: 0, scale: '.94' }, { opacity: 1, scale: '1' }], { duration: 220, easing: 'cubic-bezier(.16,1,.3,1)' });
    if (this.live.textContent !== text) this.live.textContent = text;
    if (this.tip) { this.tipRead += dt; if (this.tipRead >= 2.5 && countsOnRead(this.tip)) this.countTip(); }
    const card = { left: place.x, top: place.y, width: this.bubble.offsetWidth || r.width, height: this.bubble.offsetHeight || r.height };
    const arrow = point && placeArrow(innerWidth, innerHeight, size, card, point, obstacles);
    this.bubble.dataset.targeted = String(!!arrow);
    if (arrow) {
      this.marker.hidden = false;
      this.marker.style.left = `${arrow.x}px`; this.marker.style.top = `${arrow.y}px`;
      this.marker.style.setProperty('--angle', `${arrow.angle}rad`);
      // QA: the world spot lets the walk re-project the target through its own camera path.
      Object.assign(this.marker.dataset, { targetX: String(point.x), targetY: String(point.y), worldX: String(world?.x ?? ''), worldZ: String(world?.y ?? ''),
        angle: String(arrow.angle), side: arrow.side, motion: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'still' : 'bobbing' });
    }
    // Observation step completes on soldiers actually walking, after time to read it.
    if (STEPS[this.flow.index] === 'march') this.marchRead += dt;
    if (STEPS[this.flow.index] === 'march' && this.marchRead > 4 && walking) this.action('march');
  }
}
